import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faUser } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../../assets/images/logo-main.png';
import LogoutConfirmDialog from '../LogoutConfirmDialog';
import { logout } from '../../utils/auth';

interface HeaderProps {
  onToggleSidebar?: () => void;
}

const HeaderWrapper = styled.header`
  background: var(--card-bg);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
  height: 80px;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    height: 70px;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 15px 20px;
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  z-index: 2;
  margin-right: 60px;

  img {
    height: 50px;
    width: auto;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    margin-right: 0;
    img {
      height: 40px;
    }
  }
`;

const NavLinks = styled.nav<{ isOpen: boolean }>`
  display: flex;
  gap: 32px;
  margin-left: auto;

  @media (max-width: 768px) {
    margin-left: 0;
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    position: absolute;
    top: 70px;
    left: 0;
    right: 0;
    background: var(--white);
    padding: 20px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    flex-direction: column;
    align-items: center;
    gap: 20px;
    z-index: 1000;
    max-height: calc(100vh - 70px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const NavItem = styled.a`
  text-decoration: none;
  color: var(--text-color);
  font-weight: 500;
  font-size: 16px;
  transition: all 0.3s ease;
  padding: 8px 0;
  cursor: pointer;

  &:hover {
    color: var(--primary-color);
  }

  @media (max-width: 768px) {
    font-size: 18px;
    width: 100%;
    text-align: center;
    padding: 12px;

    &:hover {
      background: var(--light-color);
      border-radius: var(--border-radius);
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: var(--text-color);
  font-size: 24px;
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;

  @media (max-width: 768px) {
    display: block;
  }
`;

const LoginButton = styled.button`
  background: var(--gradient);
  color: var(--white);
  padding: 10px 24px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  margin-left: 32px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 15px rgba(255, 77, 0, 0.2);
  }

  @media (max-width: 768px) {
    margin: 20px 0 0;
    width: 100%;
  }
`;

const ProfileSection = styled.div`
  position: relative;
`;

const ProfileButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: var(--text-color);
  font-size: 24px;
  margin-left: 32px;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const ProfileDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: var(--white);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  border-radius: var(--border-radius);
  padding: 10px;
  z-index: 2;
`;

const DropdownItem = styled.a`
  display: block;
  text-decoration: none;
  color: var(--text-color);
  font-weight: 500;
  font-size: 16px;
  padding: 8px 0;
  cursor: pointer;

  &:hover {
    color: var(--primary-color);
  }

  @media (max-width: 768px) {
    font-size: 18px;
    width: 100%;
    text-align: center;
    padding: 12px;

    &:hover {
      background: var(--light-color);
      border-radius: var(--border-radius);
    }
  }
`;

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const token = localStorage.getItem('token');
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownRef]);

  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const toggleMenu = () => {
    if (onToggleSidebar) {
      onToggleSidebar();
    }
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogoutClick = () => {
    console.log('Attempting to show logout dialog');
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    console.log('Confirming logout');
    setShowLogoutConfirm(false);
    logout();
  };

  const handleLogoutCancel = () => {
    console.log('Cancelling logout');
    setShowLogoutConfirm(false);
  };

  useEffect(() => {
    console.log('showLogoutConfirm state changed:', showLogoutConfirm);
  }, [showLogoutConfirm]);

  return (
    <div style={{ position: 'relative' }}>
      <HeaderWrapper style={{
        background: scrolled ? 'var(--white)' : 'var(--card-bg)',
        boxShadow: scrolled ? '0 2px 10px rgba(0,0,0,0.1)' : 'none'
      }}>
        <HeaderContent>
          <Logo to="/">
            <img src={logoImage} alt="GoHighLevel" />
          </Logo>
          <NavLinks isOpen={isMenuOpen}>
            <NavItem onClick={() => scrollToSection('hero')}>Home</NavItem>
            <NavItem onClick={() => scrollToSection('features')}>Features</NavItem>
            <NavItem onClick={() => scrollToSection('pricing')}>Pricing</NavItem>
            {/* <NavItem onClick={() => scrollToSection('automation')}>Automation</NavItem> */}
            <NavItem onClick={() => scrollToSection('contact')}>Contact</NavItem>
            {token ? (
              <ProfileSection ref={profileDropdownRef}>
                <ProfileButton onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}>
                  <FontAwesomeIcon icon={faUser} />
                </ProfileButton>
                {isProfileDropdownOpen && (
                  <ProfileDropdown>
                    <DropdownItem as={Link} to="/profile">Profile</DropdownItem>
                    <DropdownItem as={Link} to="/dashboard">Dashboard</DropdownItem>
                    <DropdownItem as="button" onClick={() => {
                      setIsProfileDropdownOpen(false);
                      handleLogoutClick();
                    }}>
                      Logout
                    </DropdownItem>
                  </ProfileDropdown>
                )}
              </ProfileSection>
            ) : (
              <LoginButton onClick={() => navigate('/login')}>Login</LoginButton>
            )}
          </NavLinks>
          <MenuButton onClick={toggleMenu}>
            <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
          </MenuButton>
        </HeaderContent>
      </HeaderWrapper>

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </div>
  );
};

export default Header; 